import React from 'react'
import { Link } from 'react-router-dom'
import { Code, ExternalLink, Copy, Check, Droplets, Palette, Settings, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSEO } from '../hooks/useSEO'
import EmbeddableWaterLevelWidget from '../components/EmbeddableWaterLevelWidget'

const DeveloperGuide: React.FC = () => {
  const { t } = useTranslation()
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null)

  useSEO({
    title: 'Developer Guide - Water Level Widget Embedding - Chiang Mai Living Hub',
    description: 'Learn how to embed the Ping River water level monitoring widget on your website using iframe. Customizable options including themes, layouts, and data display.',
    keywords: 'embed widget, iframe, water level, ping river, developer guide, chiang mai, api'
  })

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedCode(id)
      setTimeout(() => setCopiedCode(null), 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const baseUrl = window.location.origin
  const embedUrl = `${baseUrl}/embed`

  const codeExamples = [
    {
      id: 'basic',
      title: 'Basic Embedding',
      description: 'Standard widget with all features enabled',
      code: `<iframe 
  src="${embedUrl}" 
  width="600" 
  height="400"
  frameborder="0"
  title="Ping River Water Level Monitor">
</iframe>`,
      preview: true,
      previewProps: {}
    },
    {
      id: 'compact',
      title: 'Compact Widget',
      description: 'Space-efficient version for sidebars or small spaces',
      code: `<iframe 
  src="${embedUrl}?compact=true" 
  width="400" 
  height="160"
  frameborder="0"
  title="Ping River Water Level - Compact">
</iframe>`,
      preview: true,
      previewProps: { compact: true }
    },
         {
       id: 'dark',
       title: 'Dark Theme',
       description: 'Redesigned dark theme with gradient backgrounds and improved contrast for modern dark websites',
       code: `<iframe 
  src="${embedUrl}?theme=dark" 
  width="600" 
  height="400"
  frameborder="0"
  title="Ping River Water Level - Dark Theme">
</iframe>`,
       preview: true,
       previewProps: { theme: 'dark' as const }
     },
    {
      id: 'minimal',
      title: 'Minimal Widget',
      description: 'Compact with hidden header for maximum space efficiency',
      code: `<iframe 
  src="${embedUrl}?compact=true&hideHeader=true" 
  width="350" 
  height="120"
  frameborder="0"
  title="Ping River Water Level - Minimal">
</iframe>`,
      preview: true,
      previewProps: { compact: true, hideHeader: true }
    },
         {
       id: 'water-only',
       title: 'Water Level Only',
       description: 'Show only water level data without rainfall information',
       code: `<iframe 
  src="${embedUrl}?rainfall=false" 
  width="500" 
  height="350"
  frameborder="0"
  title="Ping River Water Level Only">
</iframe>`,
       preview: true,
       previewProps: { showRainfall: false }
     },
     {
       id: 'with-cctv',
       title: 'With Live CCTV Feed',
       description: 'Include real-time CCTV camera feed from TP.1 Station',
       code: `<iframe 
  src="${embedUrl}?cctv=true" 
  width="600" 
  height="500"
  frameborder="0"
  title="Ping River Monitor with CCTV">
</iframe>`,
       preview: true,
       previewProps: { showCctv: true }
     },
     {
       id: 'compact-cctv',
       title: 'Compact with CCTV',
       description: 'Space-efficient widget with live camera feed',
       code: `<iframe 
  src="${embedUrl}?compact=true&cctv=true" 
  width="400" 
  height="300"
  frameborder="0"
  title="Compact Ping River Monitor with CCTV">
</iframe>`,
       preview: true,
       previewProps: { compact: true, showCctv: true }
     }
  ]

  const parameters = [
    {
      name: 'compact',
      type: 'boolean',
      default: 'false',
      description: 'Enable compact layout for smaller spaces'
    },
         {
       name: 'theme',
       type: 'string',
       default: 'light',
       description: 'Widget theme: "light" or "dark" (redesigned with gradients and improved contrast)'
     },
         {
       name: 'rainfall',
       type: 'boolean',
       default: 'true',
       description: 'Show rainfall data alongside water level'
     },
     {
       name: 'cctv',
       type: 'boolean',
       default: 'false',
       description: 'Include live CCTV camera feed from TP.1 Station (auto-refreshes every 30 seconds)'
     },
    {
      name: 'hideHeader',
      type: 'boolean',
      default: 'false',
      description: 'Hide the header title for minimal appearance'
    },
    {
      name: 'width',
      type: 'string',
      default: 'auto',
      description: 'Custom width (e.g., "400px", "100%")'
    },
    {
      name: 'height',
      type: 'string',
      default: 'auto',
      description: 'Custom height (e.g., "300px", "auto")'
    }
  ]

  const responsiveSizes = [
    {
      name: 'Desktop Full',
      width: '600px',
      height: '400px',
      useCase: 'Main content area, blog posts'
    },
    {
      name: 'Desktop Sidebar',
      width: '350px',
      height: '300px',
      useCase: 'Sidebars, secondary content'
    },
    {
      name: 'Mobile Friendly',
      width: '100%',
      height: '350px',
      useCase: 'Responsive mobile layouts'
    },
    {
      name: 'Compact Banner',
      width: '400px',
      height: '160px',
      useCase: 'Header banners, compact spaces'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Code className="h-8 w-8 text-blue-500" />
            <div>
                          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('developerGuide.title')}</h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mt-1">{t('developerGuide.subtitle')}</p>
            </div>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Droplets className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-blue-900 dark:text-blue-200">{t('developerGuide.realTimeMonitoring')}</h3>
                <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
                  {t('developerGuide.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Start */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('developerGuide.quickStart.title')}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('developerGuide.quickStart.copyCode')}</h3>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 relative">
                <button
                  onClick={() => copyToClipboard(codeExamples[0].code, 'quickstart')}
                  className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  {copiedCode === 'quickstart' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
                <pre className="text-sm text-gray-800 dark:text-gray-200 overflow-x-auto">
                  <code>{codeExamples[0].code}</code>
                </pre>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('developerGuide.quickStart.pasteHtml')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                {t('developerGuide.quickStart.instructions')}
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-green-800 text-sm font-medium">✓ {t('developerGuide.quickStart.features.0')}</p>
                <p className="text-green-800 text-sm font-medium">✓ {t('developerGuide.quickStart.features.1')}</p>
                <p className="text-green-800 text-sm font-medium">✓ {t('developerGuide.quickStart.features.2')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Configuration Examples */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <Settings className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Configuration Examples</h2>
          </div>
          
                     <div className="space-y-8">
             {codeExamples.map((example) => (
               <div key={example.id} className="border-b border-gray-200 last:border-b-0 pb-8 last:pb-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {example.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{example.description}</p>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 relative">
                      <button
                        onClick={() => copyToClipboard(example.code, example.id)}
                        className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                      >
                        {copiedCode === example.id ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <pre className="text-sm text-gray-800 dark:text-gray-200 overflow-x-auto pr-8">
                        <code>{example.code}</code>
                      </pre>
                    </div>
                  </div>
                  
                  {example.preview && (
                    <div>
                      <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">Live Preview</h4>
                                              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <EmbeddableWaterLevelWidget {...example.previewProps} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* URL Parameters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <ExternalLink className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">URL Parameters</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700">
                  <th className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Parameter
                  </th>
                  <th className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Type
                  </th>
                  <th className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Default
                  </th>
                  <th className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {parameters.map((param, index) => (
                  <tr key={param.name} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                    <td className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-sm font-mono text-blue-600 dark:text-blue-400">
                      {param.name}
                    </td>
                    <td className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {param.type}
                    </td>
                    <td className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-300">
                      {param.default}
                    </td>
                    <td className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {param.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Example URL with Parameters:</h4>
            <code className="text-sm text-blue-800 bg-blue-100 px-2 py-1 rounded">
              {embedUrl}?compact=true&theme=dark&rainfall=false
            </code>
          </div>
        </div>

        {/* Responsive Sizing Guide */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <Palette className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Recommended Sizes</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {responsiveSizes.map((size, index) => (
                              <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{size.name}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Dimensions:</span>
                    <span className="text-sm font-mono text-gray-900 dark:text-gray-100">{size.width} × {size.height}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Best for:</span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">{size.useCase}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Details */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <Globe className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Technical Details</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Data Source</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li>• Water level data from TP.1 Station via RID API</li>
                <li>• Rainfall data from meteorological sensors</li>
                <li>• Updates every 15 minutes automatically</li>
                <li>• Historical data available for trends</li>
              </ul>
            </div>
            
                          <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Performance</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li>• Lightweight: ~50KB total load size</li>
                <li>• Mobile optimized and responsive</li>
                <li>• Graceful error handling</li>
                <li>• HTTPS secure connection</li>
              </ul>
            </div>
            
                          <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Browser Support</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li>• Chrome, Firefox, Safari, Edge</li>
                <li>• Mobile browsers (iOS Safari, Chrome Mobile)</li>
                <li>• Internet Explorer 11+ (limited features)</li>
              </ul>
            </div>
            
                          <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Security</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li>• No user data collection</li>
                <li>• No cookies or tracking</li>
                <li>• CORS-enabled for iframe embedding</li>
                <li>• Content Security Policy compliant</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CSS Customization */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">CSS Styling</h2>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
            You can apply additional styling to the iframe container to better integrate with your website design:
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4 relative">
            <button
              onClick={() => copyToClipboard(`/* Example CSS for iframe styling */
.water-level-widget {
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  max-width: 100%;
}

/* Responsive iframe */
@media (max-width: 768px) {
  .water-level-widget {
    width: 100% !important;
    height: 350px !important;
  }
}`, 'css')}
              className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              {copiedCode === 'css' ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
            <pre className="text-sm text-gray-800 dark:text-gray-200 overflow-x-auto">
              <code>{`/* Example CSS for iframe styling */
.water-level-widget {
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  max-width: 100%;
}

/* Responsive iframe */
@media (max-width: 768px) {
  .water-level-widget {
    width: 100% !important;
    height: 350px !important;
  }
}`}</code>
            </pre>
          </div>
          
          <div className="mt-4">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Apply to your iframe:</h4>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <code className="text-sm text-gray-800 dark:text-gray-200">
                {`<iframe class="water-level-widget" src="${embedUrl}" ...></iframe>`}
              </code>
            </div>
          </div>
        </div>

        {/* Support & Contact */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Support & Contact</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                                                              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Need Help?</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                If you have questions about implementing the widget or need custom features, 
                we're here to help.
              </p>
              <div className="space-y-2">
                <Link
                  to="/"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View Full Monitor
                </Link>
                <br />
                <a
                  href="https://github.com/your-repo/issues"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Report Issues
                </a>
              </div>
            </div>
            
            <div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">License & Usage</h3>
                              <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <p>• Free for non-commercial use</p>
                <p>• Attribution appreciated but not required</p>
                <p>• No rate limits or API restrictions</p>
                <p>• Widget may be updated without notice</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeveloperGuide 